/**
 * The account exchange the shell runs before it can load a single plugin.
 *
 * A served deployment answers `/plugins/*` only to a caller that holds a
 * session, so the shell has to establish one before the plugin boot begins — and
 * it has to do that with no plugin loaded, which is why this code is
 * shell-owned. Every function takes its `fetch`, so a test drives the exchange
 * without a server.
 * @module @deepseek-ai/dsh-client-web/src/auth-api
 */

// Type-only: the account fact and the handoff this exchange produces are the
// account package's contract, which the graph reads as `ctx.account`.
import type { AccountFact, AccountHandoff } from '@deepseek-ai/dsh-client-account/client'

/** What the shell does after probing the account surface. */
export type AuthState =
  /** No account surface is served: boot as the single-user deployment did. */
  | { kind: 'open' }
  /** Accounts are served and this browser holds a session. */
  | { kind: 'signed-in'; account: AccountFact }
  /**
   * Accounts are served and no session is held; `needsBootstrap` means none
   * exists yet. `verificationRequired` is the deployment's own answer to whether
   * a registration has to prove its phone number with a delivered code, and
   * `codeResendSeconds` is the interval it enforces between two codes for one
   * phone and purpose. Both come from the surface rather than from a build flag,
   * because the provider a deployment mounts is what decides them.
   */
  | {
    kind: 'account-required'
    needsBootstrap: boolean
    verificationRequired: boolean
    codeResendSeconds: number
  }

/** One sign-in, registration, or reset outcome. */
export type AuthResult = { ok: true } | { ok: false; message: string }

/** A registration outcome; `pending` means the account exists but is not active. */
export type RegistrationResult = { ok: true; pending: boolean } | { ok: false; message: string }

/**
 * A verification-code request; `code` is present only when the deployment
 * delivers manually. `answered` says whether the surface answered at all: one
 * that refused has still started the resend window, while a request that never
 * arrived changed nothing.
 */
export type CodeResult =
  | { ok: true; code?: string }
  | { ok: false; message: string; answered: boolean }

/**
 * What this shell asks for a code: proving a phone number at registration, or
 * proving one to replace a password. The account service scopes a code to one
 * purpose, so a code delivered for registration can never be spent on a reset.
 */
export type CodePurpose = 'register' | 'reset-password'

/** The fields the sign-in, bootstrap, and registration forms carry. */
export interface Credentials {
  /** Account name. */
  username: string
  /** Plaintext password as typed; the transport decides how it travels. */
  password: string
  /** Display name, when the form collected one. */
  realName?: string
  /** Phone number, when the form collected one. */
  phone?: string
  /** Invitation code, for an invited registration. */
  invite?: string
  /** The code delivered to `phone`, where the deployment requires one to register. */
  code?: string
}

/** Copy for every refusal the account surface can report by code. */
const REFUSAL_MESSAGE: Readonly<Record<string, string>> = {
  'bad-credentials': '账号或密码不正确',
  'bad-token': '登录状态已失效，请重新登录',
  'inactive-account': '该账号尚未启用或已停用，请联系管理员',
  'unknown-account': '账号不存在',
  'duplicate-account': '该账号已被注册',
  'weak-password': '密码强度不足：至少 8 位',
  'bad-code': '验证码不正确或已过期',
  'code-throttled': '验证码发送过于频繁，请稍后再试',
  'verification-required': '该部署要求验证手机号，请获取验证码后完成注册',
  'delivery-failed': '验证码短信发送失败，请稍后重试或联系管理员',
  'login-throttled': '尝试次数过多，请稍后再试',
  'plaintext-password-not-accepted': '该部署要求安全连接（HTTPS）才能提交密码',
  'missing-username': '请填写账号',
  'missing-password': '请填写密码',
  'missing-new-password': '请填写新密码',
  'missing-phone-or-purpose': '请填写手机号',
  'missing-phone-or-code': '请填写手机号与验证码',
}

/** Shown for a refusal the browser does not recognize, and for a body that carried none. */
const UNKNOWN_REFUSAL = '操作失败，请稍后重试'

/** Shown when the request never reached the server. */
const UNREACHABLE = '无法连接到服务器，请确认服务正在运行'

/** One parsed response body, or undefined when the response is not this surface's JSON. */
type JsonBody = Record<string, unknown> | undefined

/**
 * Read one JSON object body.
 * @param response - the response to read.
 * @returns the object, or undefined when the body is absent, malformed, or not an object.
 */
async function readJson(response: Response): Promise<JsonBody> {
  try {
    const parsed: unknown = await response.json()
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? parsed as Record<string, unknown>
      : undefined
  } catch {
    // A body that is not JSON is another route's answer — a deployment that
    // serves no account surface falls back to the SPA document for `/auth/*`.
    return undefined
  }
}

/**
 * The account one session's body reports.
 *
 * A body this cannot read is the least privileged account rather than no
 * account: the state probe already established that this deployment serves
 * accounts, and "no accounts" is the reading under which every signed-in caller
 * is treated as the deployment's owner — exactly the surfaces that reading
 * would reveal. Server-side authorization remains the authority either way.
 * @param value - the `user` field of a `/auth/me` body.
 * @returns the account fact, with only the fields the surface actually reported.
 */
function accountOf(value: unknown): AccountFact {
  if (typeof value !== 'object' || value === null) return { role: 'viewer' }
  const record = value as Record<string, unknown>
  const username = typeof record.username === 'string' && record.username !== '' ? record.username : undefined
  const role = record.role === 'admin' || record.role === 'auditor' ? record.role : 'viewer'
  return { role, ...username === undefined ? {} : { username } }
}

/**
 * Read the account behind the current session.
 * @param fetchImpl - the platform fetch.
 * @returns the account, or undefined when this surface answered no session.
 */
export async function readAccount(fetchImpl: typeof fetch): Promise<AccountFact | undefined> {
  let response: Response
  try {
    response = await fetchImpl('/auth/me')
  } catch {
    // A request that never arrived is no evidence of a session.
    return undefined
  }
  if (!response.ok) return undefined
  const body = await readJson(response)
  return accountOf(body?.user)
}

/**
 * Resolve whether the shell must sign a caller in before booting plugins, and
 * which account it is booting for.
 * @param fetchImpl - the platform fetch.
 * @returns the state the boot chain acts on.
 */
export async function probeAuth(fetchImpl: typeof fetch): Promise<AuthState> {
  let state: JsonBody
  try {
    state = await readJson(await fetchImpl('/auth/state'))
  } catch {
    // A probe that cannot reach the server at all is no evidence of a served
    // deployment; the boot chain reports its own failure loudly.
    return { kind: 'open' }
  }
  if (state?.ok !== true) return { kind: 'open' }
  const account = await readAccount(fetchImpl)
  if (account !== undefined) return { kind: 'signed-in', account }
  const resendSeconds = state.codeResendSeconds
  return {
    kind: 'account-required',
    needsBootstrap: state.needsBootstrap === true,
    // A surface that does not answer these two is one whose registrations need no
    // code; reading the interval as zero then keeps the card from counting down a
    // window nothing reported.
    verificationRequired: state.verificationRequired === true,
    codeResendSeconds: typeof resendSeconds === 'number' && resendSeconds > 0 ? resendSeconds : 0,
  }
}

/**
 * The handoff a probed state records for the client plugin graph.
 * @param state - the state the boot chain acted on.
 * @returns the account the graph publishes, or the unserved kind when no account
 * surface is behind this page.
 */
export function accountHandoffOf(state: AuthState): AccountHandoff {
  return state.kind === 'signed-in' ? { kind: 'account', account: state.account } : { kind: 'unserved' }
}

/**
 * Base64 of one ciphertext.
 * @param bytes - the bytes to encode.
 * @returns the base64 text.
 */
function bytesToBase64(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary)
}

/**
 * The DER bytes inside one SPKI PEM.
 * @param pem - the PEM text.
 * @returns the decoded bytes.
 */
function pemToBytes(pem: string): Uint8Array<ArrayBuffer> {
  const binary = atob(pem.replace(/-----(?:BEGIN|END) PUBLIC KEY-----/g, '').replace(/\s+/g, ''))
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index)
  return bytes
}

/**
 * The password fields one request carries: ciphertext when the browser can
 * encrypt with the deployment's published key, the plaintext field otherwise.
 * @param fetchImpl - the platform fetch.
 * @param password - the password as typed.
 * @returns the fields to merge into the request body.
 */
async function passwordFields(fetchImpl: typeof fetch, password: string): Promise<Record<string, string>> {
  const subtle = globalThis.crypto?.subtle
  // A browser outside a secure context has no WebCrypto at all, which is the
  // plain-HTTP LAN deployment the account surface accepts plaintext for.
  if (subtle === undefined) return { password }
  try {
    const published = await readJson(await fetchImpl('/auth/public-key'))
    const pem = typeof published?.publicKey === 'string' ? published.publicKey : undefined
    if (pem === undefined) return { password }
    const key = await subtle.importKey('spki', pemToBytes(pem), { name: 'RSA-OAEP', hash: 'SHA-256' }, false, ['encrypt'])
    const sealed = await subtle.encrypt({ name: 'RSA-OAEP' }, key, new TextEncoder().encode(password))
    return { passwordEncrypted: bytesToBase64(new Uint8Array(sealed)) }
  } catch {
    // A vault this browser cannot use is not a reason to refuse sign-in: the
    // deployment decides whether it accepts the plaintext field.
    return { password }
  }
}

/** One POST and its parsed body, with status 0 standing for a request that never arrived. */
interface PostResult {
  /** HTTP status, or 0 when the request could not be sent. */
  status: number
  /** The parsed body, when the response carried JSON. */
  body: JsonBody
}

/**
 * POST one JSON body.
 * @param fetchImpl - the platform fetch.
 * @param path - the route under the same origin.
 * @param body - the JSON body.
 * @returns the status and parsed body.
 */
async function postJson(fetchImpl: typeof fetch, path: string, body: Record<string, unknown>): Promise<PostResult> {
  try {
    const response = await fetchImpl(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    })
    return { status: response.status, body: await readJson(response) }
  } catch {
    return { status: 0, body: undefined }
  }
}

/**
 * The message one response carries.
 * @param result - the status and body.
 * @returns the refusal copy.
 */
function messageOf(result: PostResult): string {
  if (result.status === 0) return UNREACHABLE
  const code = result.body?.code
  return typeof code === 'string' ? REFUSAL_MESSAGE[code] ?? UNKNOWN_REFUSAL : UNKNOWN_REFUSAL
}

/**
 * One outcome from a completed request.
 * @param result - the status and body.
 * @returns success, or the message to show.
 */
function outcomeOf(result: PostResult): AuthResult {
  return result.status === 200 ? { ok: true } : { ok: false, message: messageOf(result) }
}

/**
 * The optional identity fields a request carries.
 * @param credentials - the collected values.
 * @returns the non-empty fields only.
 */
function identityFields(credentials: Credentials): Record<string, string> {
  return {
    ...credentials.realName === undefined || credentials.realName === '' ? {} : { realName: credentials.realName },
    ...credentials.phone === undefined || credentials.phone === '' ? {} : { phone: credentials.phone },
  }
}

/**
 * Sign in with an account name and password.
 * @param fetchImpl - the platform fetch.
 * @param credentials - the typed account name and password.
 * @returns success once the session cookie is set.
 */
export async function signIn(fetchImpl: typeof fetch, credentials: Credentials): Promise<AuthResult> {
  const result = await postJson(fetchImpl, '/auth/login', {
    username: credentials.username,
    ...await passwordFields(fetchImpl, credentials.password),
  })
  return outcomeOf(result)
}

/**
 * Create the first administrator of a deployment that has no account yet.
 * @param fetchImpl - the platform fetch.
 * @param credentials - the account to create.
 * @returns success once the new administrator is signed in.
 */
export async function bootstrapAdmin(fetchImpl: typeof fetch, credentials: Credentials): Promise<AuthResult> {
  const result = await postJson(fetchImpl, '/auth/bootstrap', {
    username: credentials.username,
    ...identityFields(credentials),
    ...await passwordFields(fetchImpl, credentials.password),
  })
  return outcomeOf(result)
}

/**
 * Register an account, with whatever proof of identity the deployment requires:
 * an invitation, or a code delivered to the phone number it presents.
 * @param fetchImpl - the platform fetch.
 * @param credentials - the account to create, including its invitation or code.
 * @returns whether the account is active already, or the message to show.
 */
export async function registerAccount(fetchImpl: typeof fetch, credentials: Credentials): Promise<RegistrationResult> {
  const result = await postJson(fetchImpl, '/auth/register', {
    username: credentials.username,
    ...identityFields(credentials),
    ...credentials.invite === undefined || credentials.invite === '' ? {} : { invite: credentials.invite },
    ...credentials.code === undefined || credentials.code === '' ? {} : { code: credentials.code },
    ...await passwordFields(fetchImpl, credentials.password),
  })
  if (result.status !== 200) return { ok: false, message: messageOf(result) }
  const user = result.body?.user
  const status = typeof user === 'object' && user !== null ? (user as Record<string, unknown>).status : undefined
  return { ok: true, pending: status !== 'active' }
}

/**
 * Ask for a verification code for one phone number and purpose.
 * @param fetchImpl - the platform fetch.
 * @param request - the phone number, and what the code is being issued for.
 * @returns the code when the deployment delivers it manually, or the message to
 * show and whether the surface answered at all.
 */
export async function requestCode(
  fetchImpl: typeof fetch,
  request: { phone: string; purpose: CodePurpose },
): Promise<CodeResult> {
  const result = await postJson(fetchImpl, '/auth/send-code', { phone: request.phone, purpose: request.purpose })
  if (result.status !== 200) return { ok: false, message: messageOf(result), answered: result.status !== 0 }
  const code = result.body?.code
  return typeof code === 'string' ? { ok: true, code } : { ok: true }
}

/**
 * Set a new password from a delivered code.
 * @param fetchImpl - the platform fetch.
 * @param request - the phone number, the code, and the new password.
 * @returns success once the password is replaced.
 */
export async function resetPassword(
  fetchImpl: typeof fetch,
  request: { phone: string; code: string; password: string },
): Promise<AuthResult> {
  const result = await postJson(fetchImpl, '/auth/reset-password', {
    phone: request.phone,
    code: request.code,
    newPassword: request.password,
  })
  return outcomeOf(result)
}
